import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AuthShell from '../components/auth/AuthShell';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { authService } from '../services/authService';
import { getApiErrorMessage } from '../lib/api';
import GoogleSignInButton, { GOOGLE_CLIENT_ID } from '../components/auth/GoogleSignInButton';

// Turnstile widgets are locked to specific hostnames in the Cloudflare
// dashboard (app.ceben.net/ceben.net for the real key) — they silently
// refuse to render on localhost. Cloudflare's official "always passes"
// test site key (public, documented, not a secret) works on any hostname
// including localhost, so local dev can actually exercise this widget
// instead of it just never appearing. Set via REACT_APP_TURNSTILE_SITE_KEY
// at build time for production, same pattern as REACT_APP_API_URL.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

const Register: React.FC = () => {
  const { t } = useTranslation();
  const { googleLogin } = useAuthStore();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    workspaceName: '',
    timezone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setFormData(prev => ({ ...prev, timezone: detectedTimezone || '' }));
  }, []);

  // Explicit render, not the <div class="cf-turnstile" data-sitekey=...>
  // auto-scan approach: Turnstile's script scans the DOM once on load, and
  // in a React SPA that can easily run before this component has actually
  // mounted its container div, silently leaving the widget unrendered
  // forever. Rendering explicitly once window.turnstile is confirmed ready
  // (polling briefly, since the script tag is async/defer) sidesteps that
  // race entirely.
  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;

    const renderWidget = () => {
      if (cancelled) return;
      const turnstile = (window as any).turnstile;
      if (turnstile && turnstileContainerRef.current) {
        turnstileWidgetIdRef.current = turnstile.render(turnstileContainerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => setTurnstileToken(token)
        });
      } else {
        pollTimer = setTimeout(renderWidget, 100);
      }
    };
    renderWidget();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      const turnstile = (window as any).turnstile;
      if (turnstile && turnstileWidgetIdRef.current) {
        turnstile.remove(turnstileWidgetIdRef.current);
      }
    };
  }, []);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name) {
      newErrors.name = t('errors.required');
    }

    if (!formData.email) {
      newErrors.email = t('errors.required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('auth.invalidEmail');
    }

    if (!formData.password) {
      newErrors.password = t('auth.passwordRequired');
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).{8,}$/.test(formData.password)) {
      newErrors.password = t('auth.passwordComplexity');
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('errors.required');
    } else if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = t('auth.passwordMismatch');
    }

    if (!formData.workspaceName) {
      newErrors.workspaceName = t('errors.required');
    }

    if (!turnstileToken) {
      newErrors.general = t('auth.turnstileRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGoogleCredential = async (idToken: string) => {
    setIsLoading(true);
    setErrors({});
    try {
      // Unlike the password flow, Google sign-up/sign-in completes
      // immediately (Google already verified the email — see
      // AuthService.googleLogin) — no "check your email" screen, straight
      // into the app.
      await googleLogin(idToken);
      navigate('/dashboard');
    } catch (error) {
      console.error('Google sign-up error:', error);
      setErrors({ general: getApiErrorMessage(error, t('auth.registerError')) });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      await authService.register(formData.email, formData.name, formData.password, formData.workspaceName, turnstileToken, formData.timezone);
      setIsRegistered(true);
    } catch (error) {
      console.error('Register error:', error);
      if (turnstileWidgetIdRef.current) {
        (window as any).turnstile?.reset(turnstileWidgetIdRef.current);
      }
      setTurnstileToken('');
      setErrors({ general: getApiErrorMessage(error, t('auth.registerError')) });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <AuthShell title={t('auth.registerTitle')} subtitle={t('auth.registerSubtitle')}>

        {isRegistered ? (
          /* Success screen */
          <div className="space-y-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8">
            <div className="flex items-start gap-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/40 rounded-xl p-4">
              <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
              <p className="text-sm text-green-700 dark:text-green-300">
                {t('auth.registerSuccessMessage')}
              </p>
            </div>
            <div className="text-center">
              <Link
                to="/login"
                className="font-medium text-brand-700 hover:text-brand-600"
              >
                {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        ) : (
          /* Form */
          <form className="space-y-6 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8" onSubmit={handleSubmit}>
            {errors.general && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 rounded-xl p-4">
                <p className="text-sm text-red-600 dark:text-red-400">{errors.general}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Full Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.fullName')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`input-field ${
                      errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.email')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`input-field ${
                      errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                    placeholder="Enter your email"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.password')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`input-field pr-10 ${
                      errors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.confirmPassword')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`input-field pr-10 ${
                      errors.confirmPassword ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>

              {/* Workspace Name */}
              <div>
                <label htmlFor="workspaceName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.workspaceName')}
                </label>
                <div className="mt-1 relative">
                  <input
                    id="workspaceName"
                    name="workspaceName"
                    type="text"
                    autoComplete="organization"
                    required
                    value={formData.workspaceName}
                    onChange={handleInputChange}
                    className={`input-field ${
                      errors.workspaceName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                    placeholder="Enter your workspace name"
                  />
                  {errors.workspaceName && (
                    <p className="mt-1 text-sm text-red-600">{errors.workspaceName}</p>
                  )}
                </div>
              </div>

              <div ref={turnstileContainerRef} />

              {/* Timezone */}
              <div>
                <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.timezone')}
                </label>
                {/* Passive by design (not a free-text field a visitor could
                    type an invalid IANA string into, not yet a full picker
                    either) — auto-detected from the browser, with a hover
                    hint pointing at where it can actually be changed.
                    title is on this wrapper, not the disabled input itself:
                    disabled form elements suppress hover events (and with
                    them the native tooltip) in some browsers. */}
                <div className="mt-1 relative" title={t('auth.timezoneChangeHint') as string}>
                  <input
                    id="timezone"
                    name="timezone"
                    type="text"
                    autoComplete="off"
                    value={formData.timezone}
                    disabled
                    className="input-field disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-brand text-sm"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-ink"></div>
                ) : (
                  <>
                    <ShieldCheck className="h-5 w-5 mr-2" />
                    {t('auth.registerTitle')}
                  </>
                )}
              </button>
            </div>

            {/* Google Sign-Up — hidden entirely if REACT_APP_GOOGLE_CLIENT_ID
                isn't configured for this build. */}
            {GOOGLE_CLIENT_ID && (
              <div className="space-y-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500">
                      {t('auth.orContinueWith')}
                    </span>
                  </div>
                </div>
                <GoogleSignInButton onCredential={handleGoogleCredential} />
              </div>
            )}

            {/* Login Link */}
            <div className="text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('auth.alreadyHaveAccount')}{' '}
                <Link to="/login" className="font-medium text-brand-700 hover:text-brand-600">
                  {t('auth.login')}
                </Link>
              </p>
            </div>
          </form>
        )}
    </AuthShell>
  );
};

export default Register;